<script setup>
import { h, ref, computed, onMounted } from 'vue';
import {
  AiBrain03Icon,
  AnalyticsUpIcon,
  BookOpen01Icon,
  Briefcase02Icon,
  Building02Icon,
  CreditCardIcon,
  DashboardSquare02Icon,
  Folder01Icon,
  InboxIcon,
  Mailbox01Icon,
  Megaphone02Icon,
  Settings02Icon,
  Tag01Icon,
  UserGroup02Icon,
  UserSquareIcon,
  WorkflowCircle03Icon,
} from '@hugeicons/core-free-icons';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { useWindowSize } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useSettingsNavConfig } from 'dashboard/composables/useSettingsNavConfig';
import { useBillingAccess } from 'dashboard/composables/useBillingAccess';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import SidebarNotificationBell from './SidebarNotificationBell.vue';
import SidebarNotificationsPanel from './SidebarNotificationsPanel.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import HugeIcon from 'next/icon/HugeIcon.vue';
import Logo from 'next/icon/Logo.vue';
import Icon from 'next/icon/Icon.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
const { isAdmin } = useAdmin();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { shouldShow } = usePolicy();
const { isBillingRestricted, loadBillingAccess } = useBillingAccess();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const globalConfig = useMapGetter('globalConfig/get');
const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);
const isNotificationsPanelOpen = ref(false);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const DESKTOP_RAIL_WIDTH = 47;
const MOBILE_SIDEBAR_WIDTH = 240;
const sidebarWidth = computed(() =>
  isMobile.value ? MOBILE_SIDEBAR_WIDTH : DESKTOP_RAIL_WIDTH
);
const isEffectivelyCollapsed = computed(() => !isMobile.value);
const useSplitSidebar = computed(() => !isMobile.value);
const isResizing = ref(false);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  loadBillingAccess();
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const closeNotificationsPanel = () => {
  isNotificationsPanelOpen.value = false;
};

const openNotifications = () => {
  isNotificationsPanelOpen.value = !isNotificationsPanelOpen.value;
};

const openHelp = () => {
  isNotificationsPanelOpen.value = false;
  window.open(globalConfig.value.brandURL || '/', '_blank');
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const sidebarHugeIcon = icon =>
  h(HugeIcon, {
    icon,
    size: 18,
    class: 'shrink-0',
  });

const findRouteByName = name => router.getRoutes().find(r => r.name === name);

const resolveRouteMeta = (to, key, fallback) => {
  if (!to) return fallback;

  if (to.params?.navigationPath) {
    return findRouteByName(to.params.navigationPath)?.meta?.[key] ?? fallback;
  }

  return router.resolve(to)?.meta?.[key] ?? fallback;
};

const isAllowedRoute = to => {
  if (!to) return true;

  return shouldShow(
    '',
    resolveRouteMeta(to, 'permissions', []),
    resolveRouteMeta(to, 'installationTypes', [])
  );
};

const flattenedChildren = children =>
  children?.flatMap(child => child.children || child) || [];

const isRouteActive = item => {
  if (!item.to) return false;

  const path = router.resolve(item.to)?.path;
  return (
    route.path === path ||
    route.path.startsWith(`${path}/`) ||
    item.activeOn?.includes(route.name)
  );
};

const getActiveChild = children =>
  flattenedChildren(children).find(isRouteActive);

const hasActiveChild = item => Boolean(getActiveChild(item.children));

const getFirstAllowedRoute = item => {
  if (item.to && isAllowedRoute(item.to)) return item.to;

  return flattenedChildren(item.children).find(child =>
    isAllowedRoute(child.to)
  )?.to;
};

const isAllowedMenuItem = item => {
  if (item.to) return isAllowedRoute(item.to);

  return flattenedChildren(item.children).some(child =>
    isAllowedRoute(child.to)
  );
};

const { settingsNavChildren } = useSettingsNavConfig();

const accountSettingsRailLeaf = computed(() => ({
  name: 'Account',
  label: t('SIDEBAR.ACCOUNT_SETTINGS'),
  icon: sidebarHugeIcon(Briefcase02Icon),
  to: accountScopedRoute('general_info_index'),
  activeOn: ['general_info_index'],
}));

const adminWorkspaceMenuItem = computed(() => ({
  name: 'AdminWorkspace',
  label: t('SIDEBAR.ACCOUNT_AND_WORKSPACE'),
  icon: sidebarHugeIcon(Settings02Icon),
  children: [accountSettingsRailLeaf.value, ...settingsNavChildren.value],
}));

const menuItems = computed(() => {
  return [
    ...(isAdmin.value
      ? [
          {
            name: 'Dashboard',
            label: t('SIDEBAR.DASHBOARD'),
            icon: sidebarHugeIcon(DashboardSquare02Icon),
            to: accountScopedRoute('home'),
            activeOn: ['home'],
          },
        ]
      : []),
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: sidebarHugeIcon(InboxIcon),
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['conversations_index', 'inbox_conversation'],
          to: accountScopedRoute('conversations_index'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Participating',
          label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: sidebarHugeIcon(Folder01Icon),
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: sidebarHugeIcon(UserGroup02Icon),
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: sidebarHugeIcon(Mailbox01Icon),
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: sidebarHugeIcon(Tag01Icon),
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-lg`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    ...(isAdmin.value
      ? [
          {
            name: 'AI Agents',
            label: t('SIDEBAR.AI_AGENTS'),
            icon: sidebarHugeIcon(AiBrain03Icon),
            activeOn: [
              'ai_agents',
              'ai_agents_templates',
              'ai_agents_template_preview',
              'ai_agents_new',
              'ai_agents_edit',
              'ai_agents_runs',
            ],
            to: accountScopedRoute('ai_agents'),
          },
        ]
      : []),
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: sidebarHugeIcon(UserSquareIcon),
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: sidebarHugeIcon(UserGroup02Icon),
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: sidebarHugeIcon(Tag01Icon),
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-lg`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: sidebarHugeIcon(Building02Icon),
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index'],
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: sidebarHugeIcon(AnalyticsUpIcon),
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.BROADCASTS'),
      icon: sidebarHugeIcon(Megaphone02Icon),
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    {
      name: 'Workflows',
      label: t('SIDEBAR.WORKFLOWS'),
      icon: sidebarHugeIcon(WorkflowCircle03Icon),
      activeOn: [
        'automation_list',
        'automation_editor_new',
        'automation_editor_edit',
      ],
      to: accountScopedRoute('automation_list'),
    },
    ...(isAdmin.value
      ? [
          {
            name: 'Portals',
            label: t('SIDEBAR.HELP_CENTER.TITLE'),
            icon: sidebarHugeIcon(BookOpen01Icon),
            children: [
              {
                name: 'Articles',
                label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
                activeOn: [
                  'portals_articles_index',
                  'portals_articles_new',
                  'portals_articles_edit',
                ],
                to: accountScopedRoute('portals_index', {
                  navigationPath: 'portals_articles_index',
                }),
              },
              {
                name: 'Categories',
                label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
                activeOn: [
                  'portals_categories_index',
                  'portals_categories_articles_index',
                  'portals_categories_articles_edit',
                ],
                to: accountScopedRoute('portals_index', {
                  navigationPath: 'portals_categories_index',
                }),
              },
              {
                name: 'Locales',
                label: t('SIDEBAR.HELP_CENTER.LOCALES'),
                activeOn: ['portals_locales_index'],
                to: accountScopedRoute('portals_index', {
                  navigationPath: 'portals_locales_index',
                }),
              },
              {
                name: 'Settings',
                label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
                activeOn: ['portals_settings_index'],
                to: accountScopedRoute('portals_index', {
                  navigationPath: 'portals_settings_index',
                }),
              },
            ],
          },
        ]
      : []),
    ...(isAdmin.value
      ? [
          {
            name: 'Billing',
            label: t('SIDEBAR.BILLING'),
            icon: sidebarHugeIcon(CreditCardIcon),
            to: accountScopedRoute('general_settings_billing'),
            activeOn: [
              'general_settings_billing',
              'general_settings_billing_plans',
              'general_settings_ai_billing',
              'general_settings_whatsapp_fees',
            ],
          },
          adminWorkspaceMenuItem.value,
        ]
      : []),
    ...(isAdmin.value
      ? []
      : [
          {
            name: 'Settings',
            label: t('SIDEBAR.SETTINGS'),
            icon: sidebarHugeIcon(Settings02Icon),
            children: settingsNavChildren.value,
          },
        ]),
  ];
});

const PRIMARY_MENU_ORDER = [
  'Dashboard',
  'Conversation',
  'Contacts',
  'AI Agents',
  'Campaigns',
  'Workflows',
  'Reports',
  'Billing',
  'AdminWorkspace',
  'Settings',
];

const BILLING_RESTRICTED_HIDDEN_MENUS = [
  'Dashboard',
  'Conversation',
  'Contacts',
  'Companies',
  'AI Agents',
  'Captain',
  'Reports',
  'Campaigns',
  'Workflows',
  'Portals',
];

const visibleMenuItems = computed(() =>
  PRIMARY_MENU_ORDER.map(name =>
    menuItems.value.find(item => item.name === name)
  )
    .filter(Boolean)
    .filter(
      item =>
        !isBillingRestricted.value ||
        !BILLING_RESTRICTED_HIDDEN_MENUS.includes(item.name)
    )
    .filter(isAllowedMenuItem)
);

const activeTopLevelItem = computed(() =>
  visibleMenuItems.value.find(
    item => isRouteActive(item) || hasActiveChild(item)
  )
);

const activePanelItem = computed(() => {
  return (
    activeTopLevelItem.value ||
    visibleMenuItems.value.find(item => item.name === expandedItem.value) ||
    visibleMenuItems.value[0]
  );
});

const onRailItemClick = item => {
  isNotificationsPanelOpen.value = false;

  if (item.children) {
    setExpandedItem(item.name);
  } else {
    expandedItem.value = null;
  }

  const targetRoute = getFirstAllowedRoute(item);
  if (targetRoute) router.push(targetRoute);
};
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <template v-if="useSplitSidebar">
      <section class="h-full min-w-0">
        <div
          class="flex flex-col items-center justify-between h-full border-r border-n-strong bg-n-slate-2 dark:bg-n-slate-2 min-w-0"
        >
          <div class="grid gap-4 justify-items-center w-full py-3">
            <SidebarAccountSwitcher
              is-collapsed
              @show-create-account-modal="emit('showCreateAccountModal')"
            />
            <ul class="grid gap-2 m-0 list-none">
              <li v-for="item in visibleMenuItems" :key="item.name">
                <div class="relative group">
                  <button
                    type="button"
                    class="relative flex items-center justify-center w-[46px] h-9 rounded-xl transition-colors duration-150"
                    :class="{
                      'text-n-slate-12 bg-n-alpha-2':
                        activePanelItem?.name === item.name,
                      'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2':
                        activePanelItem?.name !== item.name,
                    }"
                    :title="item.label"
                    @click="onRailItemClick(item)"
                  >
                    <span
                      v-if="activePanelItem?.name === item.name"
                      class="absolute rounded-full h-7 w-1.5 bg-n-slate-12 ltr:-left-1 rtl:-right-1"
                    />
                    <span
                      v-else
                      class="absolute hidden rounded-full h-6 w-1 bg-n-slate-12/40 ltr:-left-1 rtl:-right-1 group-hover:block"
                    />
                    <Icon
                      v-if="item.icon"
                      :icon="item.icon"
                      class="size-5 rounded"
                    />
                  </button>
                  <span
                    class="pointer-events-none absolute z-50 hidden px-2.5 py-1.5 text-xs font-medium whitespace-nowrap rounded-md shadow-lg ltr:left-[47px] rtl:right-[47px] top-1/2 -translate-y-1/2 bg-n-slate-12 text-n-slate-1 group-hover:block before:content-[''] before:absolute before:top-1/2 before:-translate-y-1/2 before:w-2 before:h-2 before:bg-n-slate-12 before:rotate-45 ltr:before:-left-0.5 rtl:before:-right-0.5"
                  >
                    {{ item.label }}
                  </span>
                </div>
              </li>
            </ul>
          </div>
          <div
            class="grid gap-2 justify-items-center w-full py-2 border-t border-n-weak"
          >
            <SidebarProfileMenu is-collapsed />
            <div
              v-on-click-outside="[
                closeNotificationsPanel,
                { ignore: ['.sidebar-notifications-panel'] },
              ]"
              class="relative group"
            >
              <SidebarNotificationBell
                @open-notification-panel="openNotifications"
              />
              <span
                class="pointer-events-none absolute z-50 hidden px-2.5 py-1.5 text-xs font-medium whitespace-nowrap rounded-md shadow-lg ltr:left-[47px] rtl:right-[47px] top-1/2 -translate-y-1/2 bg-n-slate-12 text-n-slate-1 group-hover:block before:content-[''] before:absolute before:top-1/2 before:-translate-y-1/2 before:w-2 before:h-2 before:bg-n-slate-12 before:rotate-45 ltr:before:-left-0.5 rtl:before:-right-0.5"
              >
                {{ t('SIDEBAR.NOTIFICATIONS') }}
              </span>
              <SidebarNotificationsPanel
                v-if="isNotificationsPanelOpen"
                class="sidebar-notifications-panel"
                @close="closeNotificationsPanel"
              />
            </div>
            <button
              type="button"
              class="relative flex items-center justify-center size-10 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 group"
              :title="t('SIDEBAR.HELP')"
              @click="openHelp"
            >
              <Icon icon="i-lucide-circle-help" class="size-5" />
              <span
                class="pointer-events-none absolute z-50 hidden px-2.5 py-1.5 text-xs font-medium whitespace-nowrap rounded-md shadow-lg ltr:left-[47px] rtl:right-[47px] top-1/2 -translate-y-1/2 bg-n-slate-12 text-n-slate-1 group-hover:block before:content-[''] before:absolute before:top-1/2 before:-translate-y-1/2 before:w-2 before:h-2 before:bg-n-slate-12 before:rotate-45 ltr:before:-left-0.5 rtl:before:-right-0.5"
              >
                {{ t('SIDEBAR.HELP') }}
              </span>
            </button>
          </div>
        </div>
      </section>
    </template>
    <template v-else>
      <section
        class="grid"
        :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
      >
        <div
          class="flex gap-2 items-center min-w-0"
          :class="{
            'justify-center px-1': isEffectivelyCollapsed,
            'px-2': !isEffectivelyCollapsed,
          }"
        >
          <template v-if="isEffectivelyCollapsed">
            <SidebarAccountSwitcher
              is-collapsed
              @show-create-account-modal="emit('showCreateAccountModal')"
            />
          </template>
          <template v-else>
            <div class="grid flex-shrink-0 place-content-center size-6">
              <Logo class="size-4" />
            </div>
            <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
            <SidebarAccountSwitcher
              class="flex-grow -mx-1 min-w-0"
              @show-create-account-modal="emit('showCreateAccountModal')"
            />
          </template>
        </div>
        <div
          class="flex gap-2"
          :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
        >
          <RouterLink
            v-if="!isEffectivelyCollapsed"
            :to="{ name: 'search' }"
            class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
          >
            <span
              class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10"
            />
            <span class="flex-grow text-start text-n-slate-10">
              {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
            </span>
            <span
              class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
            >
              {{ searchShortcut }}
            </span>
          </RouterLink>
          <RouterLink
            v-else
            :to="{ name: 'search' }"
            class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
            :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
          >
            <span class="i-lucide-search size-4 text-n-slate-11" />
          </RouterLink>
          <ComposeConversation align-position="right" @close="onComposeClose">
            <template #trigger="{ toggle, isOpen }">
              <Button
                icon="i-lucide-pen-line"
                color="slate"
                size="sm"
                class="dark:hover:!bg-n-slate-9/30"
                :class="[
                  isEffectivelyCollapsed
                    ? '!size-8 !outline-n-weak !text-n-slate-11'
                    : '!h-7 !outline-n-weak !text-n-slate-11',
                  { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
                ]"
                @click="onComposeOpen(toggle)"
              />
            </template>
          </ComposeConversation>
        </div>
      </section>
      <nav
        class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
        :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
      >
        <ul
          class="flex flex-col gap-1 m-0 list-none min-w-0"
          :class="{ 'items-center': isEffectivelyCollapsed }"
        >
          <SidebarGroup
            v-for="item in visibleMenuItems"
            :key="item.name"
            v-bind="item"
          />
        </ul>
      </nav>
      <section
        class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
      >
        <div
          class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
        />
        <SidebarChangelogCard
          v-if="
            isOnChatwootCloud &&
            !isACustomBrandedInstance &&
            !isEffectivelyCollapsed
          "
        />
        <SidebarChangelogButton
          v-if="
            isOnChatwootCloud &&
            !isACustomBrandedInstance &&
            isEffectivelyCollapsed
          "
        />
        <div
          class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
          :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
        >
          <SidebarProfileMenu :is-collapsed="isEffectivelyCollapsed" />
        </div>
      </section>
    </template>
  </aside>
</template>
